import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { isProfessional, canAccessModule, allowedModules } from "@/lib/access";
import {
  LayoutDashboard, Briefcase, Building2, FolderLock, ShieldCheck,
  BarChart3, Settings as SettingsIcon, LogOut, Menu, Network, Coins,
  Scale, TrendingUp, Search, Bell, ScrollText, UserCircle2, ChevronRight,
  Zap, Plus, CalendarClock, UploadCloud, FileText, Share2, Landmark, UserCog,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Wordmark } from "@/components/Wordmark";
import { CommandPalette } from "@/components/CommandPalette";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");

const GROUP_PRIMARY = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, mod: "overview" },
  { to: "/holdings", label: "Portfolio", icon: Briefcase, mod: "portfolio" },
  { to: "/ownership", label: "Ownership", icon: Network, mod: "ownership" },
  { to: "/alternatives", label: "Private Capital", icon: TrendingUp, mod: "private_capital" },
  { to: "/fixed-income", label: "Fixed Income", icon: Coins, mod: "fixed_income" },
];
const GROUP_OPS = [
  { to: "/entities", label: "Entities & Family", icon: Building2, mod: "entities" },
  { to: "/corporate", label: "Corporate", icon: Landmark, mod: "corporate" },
  { to: "/documents", label: "Document Vault", icon: FolderLock, mod: "documents" },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, mod: "compliance" },
  { to: "/governance", label: "Governance", icon: Scale, mod: "governance" },
  { to: "/reports", label: "Reports", icon: BarChart3, mod: "reports" },
  { to: "/professionals", label: "Professionals", icon: UserCog, adminOnly: true },
];
const ALL_NAV = [...GROUP_PRIMARY, ...GROUP_OPS, { to: "/settings", label: "Settings", icon: SettingsIcon }];

const QUICK_ACTIONS = [
  { label: "New holding", desc: "Add a portfolio position", to: "/holdings", icon: Briefcase, group: "Create" },
  { label: "New compliance task", desc: "Track a one-off obligation", to: "/compliance", icon: ShieldCheck, group: "Create" },
  { label: "New recurring schedule", desc: "Auto-generate periodic tasks", to: "/compliance", icon: CalendarClock, group: "Create" },
  { label: "Add ownership link", desc: "Extend the ownership graph", to: "/ownership", icon: Share2, group: "Create" },
  { label: "Add corporate profile", desc: "MCA data, directors & shareholders", to: "/corporate", icon: Landmark, group: "Create" },
  { label: "Submit change request", desc: "Route via maker-checker", to: "/governance", icon: Scale, group: "Create" },
  { label: "Upload document", desc: "Add to the vault", to: "/documents", icon: UploadCloud, group: "Generate" },
  { label: "Generate board pack", desc: "Export a confidential PDF", to: "/reports", icon: FileText, group: "Generate" },
];

const railLinkClass = ({ isActive }) =>
  cn(
    "group relative flex h-11 w-11 items-center justify-center rounded-xl transition-[background-color,box-shadow] duration-150",
    isActive ? "bg-[rgba(176,141,87,0.12)]" : "hover:bg-white/[0.06]"
  );

function RailIcon({ Icon, isActive }) {
  return (
    <>
      {isActive && (
        <span className="absolute -left-2 top-1.5 bottom-1.5 w-[2px] rounded-full bg-[color:var(--mk-primary)]" />
      )}
      <Icon
        className="h-[22px] w-[22px] transition-colors duration-150"
        strokeWidth={2.25}
        style={{ color: isActive ? "var(--mk-primary)" : "rgba(244,241,234,0.7)" }}
      />
    </>
  );
}

function RailLink({ item }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={item.to}
          end={item.end}
          data-testid={`app-rail-nav-${slug(item.label)}`}
          className={railLinkClass}
          aria-label={item.label}
        >
          {({ isActive }) => <RailIcon Icon={item.icon} isActive={isActive} />}
        </NavLink>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={12}
        className="border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] text-[color:var(--mk-text)]"
      >
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

export default function Layout({ children }) {
  const { user, familyOffice, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isPro = isProfessional(user);

  // Visibility rule for nav items (admin sees all; professionals only granted modules)
  const visible = useCallback((item) => {
    if (item.adminOnly) return user?.role === "admin";
    if (!item.mod) return user?.role !== "professional"; // Settings: hidden for professionals
    return canAccessModule(user, item.mod);
  }, [user]);

  const primaryNav = GROUP_PRIMARY.filter(visible);
  const opsNav = GROUP_OPS.filter(visible);
  const mobileNav = ALL_NAV.filter(visible);

  // Safe landing: bounce professionals away from modules they cannot access
  useEffect(() => {
    if (!user || user.role === "admin" || !isPro) return;
    const allowed = allowedModules(user).map((m) => m.route);
    const here = location.pathname;
    const onAllowed = allowed.some((r) => here.startsWith(r));
    if (!onAllowed && allowed.length > 0) navigate(allowed[0], { replace: true });
  }, [user, isPro, location.pathname, navigate]);

  // Cmd/Ctrl+K opens the command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const UserMenu = useCallback(
    ({ children: trigger }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)]"
        >
          <DropdownMenuLabel>
            <div className="flex items-center gap-2">
              <UserCircle2 className="h-4 w-4 text-[color:var(--mk-muted)]" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-[color:var(--mk-text)]">{user?.name}</div>
                <div className="truncate text-[11px] text-[color:var(--mk-muted)]">{user?.email}</div>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[color:var(--mk-border)]" />
          <DropdownMenuItem onClick={() => navigate("/governance")} data-testid="usermenu-audit-log">
            <ScrollText className="mr-2 h-4 w-4" /> Audit log
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="usermenu-settings">
            <SettingsIcon className="mr-2 h-4 w-4" /> Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-[color:var(--mk-border)]" />
          <DropdownMenuItem
            onClick={logout}
            data-testid="logout-button"
            className="text-[#D98A8A] focus:text-[#D98A8A]"
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
    [user, logout, navigate]
  );

  return (
    <TooltipProvider delayDuration={0}>
      <div className="app-shell-dark theme-marketing-dark relative min-h-screen text-[color:var(--mk-text)]">
        {/* ============ Icon rail (desktop) ============ */}
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-20 lg:block">
          <div className="sticky top-3 m-3 flex h-[calc(100vh-24px)] flex-col items-center rounded-[16px] border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)]/80 py-4 backdrop-blur-sm shadow-[0_18px_60px_rgba(0,0,0,0.45)]">
            <NavLink to="/dashboard" aria-label="Family Souq home" className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--mk-primary)]/15">
              <span className="font-display text-lg font-semibold text-[color:var(--mk-primary-2)]">F</span>
            </NavLink>
            <Separator className="mb-3 w-8 bg-[color:var(--mk-border)]" />

            <nav className="flex flex-1 flex-col items-center gap-1.5">
              {primaryNav.map((item) => <RailLink key={item.to} item={item} />)}
              {opsNav.length > 0 && <Separator className="my-2 w-8 bg-[color:var(--mk-border)]" />}
              {opsNav.map((item) => <RailLink key={item.to} item={item} />)}
            </nav>

            <Separator className="my-2 w-8 bg-[color:var(--mk-border)]" />
            <div className="flex flex-col items-center gap-1.5">
              {user?.role !== "professional" && (
                <RailLink item={{ to: "/settings", label: "Settings", icon: SettingsIcon }} />
              )}
              <UserMenu>
                <button
                  data-testid="rail-user-menu-button"
                  className="focus-brass mt-1 rounded-full outline-none"
                  aria-label="Account"
                >
                  <Avatar className="h-9 w-9 border border-[color:var(--mk-border-strong)]">
                    <AvatarFallback className="bg-[color:var(--mk-primary)]/20 text-xs font-semibold text-[color:var(--mk-primary-2)]">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </UserMenu>
            </div>
          </div>
        </aside>

        {/* ============ Main column ============ */}
        <div className="relative z-10 lg:pl-20">
          {/* Topbar */}
          <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-[color:var(--mk-border)] bg-[color:var(--mk-bg)]/75 px-4 backdrop-blur-md sm:px-6">
            {/* mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="-ml-1 rounded-md p-2 hover:bg-white/5 lg:hidden" data-testid="mobile-menu-button" aria-label="Menu">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-0">
                <div className="border-b border-[color:var(--mk-border)] px-5 py-4">
                  <Wordmark size="md" tone="light" />
                </div>
                <nav className="grid grid-cols-1 gap-1 p-3">
                  {mobileNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${slug(item.label)}`}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                          isActive
                            ? "bg-[rgba(176,141,87,0.12)] text-[color:var(--mk-text)]"
                            : "text-[color:var(--mk-text-2)] hover:bg-white/5"
                        )
                      }
                    >
                      <item.icon className="h-[18px] w-[18px]" strokeWidth={2.25} />
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>

            {/* family office identity */}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-display text-base font-semibold text-[color:var(--mk-text)]" data-testid="topbar-fo-name">
                  {familyOffice?.name || "Family Office"}
                </span>
                <span className="hidden rounded-full border border-[color:var(--mk-border-strong)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[color:var(--mk-primary-2)] sm:inline">
                  Live
                </span>
              </div>
              <div className="-mt-0.5 text-[11px] text-[color:var(--mk-muted)]">Base currency · INR ₹</div>
            </div>

            {/* right cluster */}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setCmdOpen(true)}
                data-testid="topbar-command-palette-trigger"
                className="group inline-flex h-9 items-center gap-2 rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] px-3 text-sm text-[color:var(--mk-muted)] transition-colors hover:border-[color:var(--mk-border-strong)] hover:text-[color:var(--mk-text-2)]"
              >
                <Search className="h-4 w-4" />
                <span className="hidden sm:inline">Search or jump to…</span>
                <kbd className="ml-1 hidden rounded border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] px-1.5 py-0.5 font-data text-[10px] sm:inline">
                  ⌘K
                </kbd>
              </button>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setDrawerOpen(true)}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-[color:var(--mk-primary)]/40 bg-[color:var(--mk-primary)]/12 text-[color:var(--mk-primary-2)] transition-colors hover:bg-[color:var(--mk-primary)]/20"
                    data-testid="topbar-quick-actions-trigger"
                    aria-label="Quick actions"
                  >
                    <Zap className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] text-[color:var(--mk-text)]">
                  Quick actions
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] text-[color:var(--mk-text-2)] transition-colors hover:text-[color:var(--mk-text)]"
                    data-testid="topbar-notifications"
                    aria-label="Notifications"
                  >
                    <Bell className="h-4 w-4" />
                    <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[color:var(--mk-primary)]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] text-[color:var(--mk-text)]">
                  Notifications
                </TooltipContent>
              </Tooltip>

              {/* mobile avatar (rail hidden) */}
              <div className="lg:hidden">
                <UserMenu>
                  <button data-testid="topbar-user-menu-button" className="focus-brass rounded-full outline-none" aria-label="Account">
                    <Avatar className="h-9 w-9 border border-[color:var(--mk-border-strong)]">
                      <AvatarFallback className="bg-[color:var(--mk-primary)]/20 text-xs font-semibold text-[color:var(--mk-primary-2)]">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </UserMenu>
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-[1400px] px-4 py-7 sm:px-6 lg:px-8">{children}</main>
        </div>

        <CommandPalette
          open={cmdOpen}
          onOpenChange={setCmdOpen}
          onQuickActions={() => { setCmdOpen(false); setDrawerOpen(true); }}
        />

        {/* Right-side contextual quick-actions drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="right" className="w-[340px] border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] p-0" data-testid="quick-actions-drawer">
            <div className="border-b border-[color:var(--mk-border)] px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[color:var(--mk-primary)]/15 text-[color:var(--mk-primary-2)]"><Zap className="h-4 w-4" /></span>
                <div>
                  <div className="font-display text-base font-semibold text-[color:var(--mk-text)]">Quick actions</div>
                  <div className="text-[11px] text-[color:var(--mk-muted)]">Jump straight into a workflow</div>
                </div>
              </div>
            </div>
            <div className="space-y-5 p-4">
              {["Create", "Generate"].map((grp) => (
                <div key={grp}>
                  <div className="mb-2 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-[color:var(--mk-muted)]">{grp}</div>
                  <div className="space-y-1.5">
                    {QUICK_ACTIONS.filter((a) => a.group === grp).map((a) => (
                      <button
                        key={a.label}
                        onClick={() => { setDrawerOpen(false); navigate(a.to); }}
                        data-testid={`quick-action-${slug(a.label)}`}
                        className="group flex w-full items-center gap-3 rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] p-3 text-left transition-colors hover:border-[color:var(--mk-border-strong)] hover:bg-white/[0.04]"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--mk-primary)]/12 text-[color:var(--mk-primary-2)]">
                          <a.icon className="h-4 w-4" strokeWidth={2.25} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-[color:var(--mk-text)]">{a.label}</span>
                          <span className="block truncate text-[11px] text-[color:var(--mk-muted)]">{a.desc}</span>
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-[color:var(--mk-muted)] transition-transform group-hover:translate-x-0.5" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}
